import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { SiteFeedbackApi } from './types';

export type CookieConsentChoice = 'accept' | 'reject' | 'custom';

export interface CookieConsentBannerProps {
  storageKey: string;
  policyVersion: string;
  privacyPath: string;
  cookiesPath: string;
  termsPath?: string;
  api?: SiteFeedbackApi;
  onChoice?: (choice: CookieConsentChoice) => void;
}

export function CookieConsentBanner({
  storageKey,
  policyVersion,
  privacyPath,
  cookiesPath,
  termsPath = '/settings/terms',
  api,
  onChoice
}: CookieConsentBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setVisible(true);
        return;
      }
      const parsed = JSON.parse(raw) as { version?: string };
      if (parsed.version !== policyVersion) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, [policyVersion, storageKey]);

  const persist = useCallback(
    async (choice: CookieConsentChoice) => {
      localStorage.setItem(storageKey, JSON.stringify({ choice, version: policyVersion, at: new Date().toISOString() }));
      setVisible(false);
      onChoice?.(choice);
      if (api) {
        try {
          await api.recordCookieConsent(choice);
        } catch {
          /* non-blocking */
        }
      }
    },
    [api, onChoice, policyVersion, storageKey]
  );

  if (!visible) return null;

  return (
    <div className="site-support-cookie-banner" role="dialog" aria-label="Cookie consent">
      <div className="site-support-cookie-banner__content">
        <strong>Cookies on this site</strong>
        <p>
          We use essential cookies for sign-in and session security. See our{' '}
          <Link to={privacyPath}>Privacy Policy</Link>, <Link to={cookiesPath}>Cookie Policy</Link>, and{' '}
          <Link to={termsPath}>Terms of Use</Link> for details. Rejecting limits non-essential functionality.
        </p>
        <div className="site-support-cookie-banner__actions">
          {/* Plain HTML — DX Button on first paint can hit React #185 under React 19. */}
          <button
            type="button"
            className="site-support-cookie-banner__btn site-support-cookie-banner__btn--outlined"
            onClick={() => void persist('reject')}
          >
            Reject
          </button>
          <button
            type="button"
            className="site-support-cookie-banner__btn site-support-cookie-banner__btn--primary"
            onClick={() => void persist('accept')}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

export function hasAcceptedCookies(storageKey: string, policyVersion: string): boolean {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { choice?: string; version?: string };
    return parsed.choice === 'accept' && parsed.version === policyVersion;
  } catch {
    return false;
  }
}
